// The DeviceBridge contract (technical plan §9.6). One interface, one implementation per shell
// plus a mock that behaves exactly like the real thing.
//
// Two rules carried over from the salvage pass shape every type here:
// - a 5-second timeout for printers and scanners;
// - a printer failure never blocks the operation: `print()` never throws. A failed job comes
//   back as a result, and is queued for reprint.

import type { LabelSize, ScanResult } from '@enp/domain';
import type { ReprintQueue } from './reprint-queue.js';

/** The 5-second rule for printers and scanners. */
export const DEVICE_TIMEOUT_MS = 5_000;

/** Which shell a bridge belongs to. */
export type BridgeKind = 'mock' | 'web' | 'electron' | 'capacitor';

/** What actually prints: a network ZPL printer, the mock, or nothing (plain browser). */
export type PrinterKind = 'network' | 'mock' | 'none';

/** A label print job. The bytes are ZPL rendered by `@enp/domain`, identical on every shell. */
export interface LabelJob {
  /** Unique per job (reprints reuse it). */
  readonly id: string;
  /** Raw bytes sent to the printer (ZPL II, UTF-8). */
  readonly zpl: Uint8Array;
  /** Plain words for the reprint list and logs, e.g. "Test label" or "Seed loaf L-K7Q-00481". */
  readonly description: string;
  /** ISO instant the job was made. */
  readonly createdAt: string;
  /** What the label is, for the print log (`label.print_logged`); never sent to the printer. */
  readonly meta?: LabelJobMeta;
}

/** What a job prints, so a print (or a reprint from the list) can be logged. */
export interface LabelJobMeta {
  kind: 'lot' | 'test';
  /** The lot on a lot label. */
  lotId?: string;
  copies: number;
  labelSize: LabelSize;
}

/** Why a print did not happen. */
export type PrintFailureReason =
  | 'timeout' // no answer within the timeout
  | 'refused' // the host answered, nothing listens on the port
  | 'unreachable' // no route to the host / unknown name
  | 'not-configured' // no printer set up on this station
  | 'unsupported' // this shell cannot print at all (plain browser)
  | 'error'; // anything else (connection reset, native error…)

export type PrintResult =
  | {
      ok: true;
      jobId: string;
      printer: PrinterKind;
      durationMs: number;
    }
  | {
      ok: false;
      jobId: string;
      printer: PrinterKind;
      reason: PrintFailureReason;
      /** Plain words for the station screen. */
      message: string;
      /** True when the job was put on the reprint list. */
      queued: boolean;
      durationMs: number;
    };

export type PrinterState = 'ready' | 'offline' | 'not-configured' | 'unsupported';

export interface PrinterStatus {
  printer: PrinterKind;
  state: PrinterState;
  /** `host:port` of a network printer. */
  target?: string;
  /** Plain words for the station screen. */
  message: string;
  /** ISO instant of the check. */
  checkedAt: string;
}

/** A network printer address. Port 9100 is the raw ZPL port on every mainstream printer. */
export interface PrinterTarget {
  host: string;
  port: number;
}

export const DEFAULT_PRINTER_PORT = 9100;

/** One code read by a scanner, already classified by the domain (`LOT:` QR, GTIN or text). */
export interface ScanRead {
  /** Exactly what the code contains. */
  text: string;
  /** Symbology as the scanner reports it, e.g. `QRCode`, `EAN13`. */
  format: string;
  /** `parseScan(text)` from `@enp/domain`. */
  parsed: ScanResult;
  source: 'camera' | 'native' | 'mock';
}

export interface ScanOptions {
  /** Stop scanning (the iterator then ends without an error). */
  signal?: AbortSignal;
  /** End after the first read (default true). */
  once?: boolean;
  /** Camera preview element (web/Electron). Without one, frames are read off-screen. */
  preview?: HTMLVideoElement;
  /** How long the camera may take to start (default 5 s). */
  startTimeoutMs?: number;
}

export type ScanErrorCode =
  | 'unsupported' // no camera API on this device
  | 'permission-denied'
  | 'no-camera'
  | 'timeout' // the camera did not start within the timeout
  | 'unavailable' // e.g. the native scanner module is still installing
  | 'error';

/** Thrown by a scan iterator when scanning cannot start or breaks. */
export class ScanError extends Error {
  override readonly name = 'ScanError';
  constructor(
    readonly code: ScanErrorCode,
    message: string,
  ) {
    super(message);
  }
}

export interface Scanner {
  /** Camera (or native) scanning. Yields reads until aborted, or once when `once` is set. */
  scan(opts?: ScanOptions): AsyncIterable<ScanRead>;
}

export interface Printer {
  readonly kind: PrinterKind;
  /** Never throws. A failure is returned (and queued for reprint), never thrown into the UI flow. */
  print(job: LabelJob): Promise<PrintResult>;
  /** Never throws; answers within the timeout. */
  status(): Promise<PrinterStatus>;
}

/** Something found on the network (V1: printers). */
export interface Device {
  id: string;
  kind: 'printer';
  name: string;
  target: PrinterTarget;
}

export interface DeviceBridge {
  readonly kind: BridgeKind;
  readonly printer: Printer;
  readonly scanner: Scanner;
  /** Printers on the LAN. */
  discover(): Promise<Device[]>;
  /** Jobs that failed and wait for a reprint. */
  readonly reprints: ReprintQueue;
}
